// usePagination — shared dashboard list-page pagination hook.
//
// Spec: docs/specs/dashboard-pagination.md §7.1.
//
// Single source of truth for (page, pageSize) state with optional URL
// query sync (`?page=3&size=50`). All dashboard list pages should
// route their server fetches through this — no more per-page ad hoc
// useState + hardcoded limits.

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Hard cap shared with the server (spec §6.1). UI clamps to this. */
export const MAX_PAGE_SIZE = 200;
/** Hard cap on offset (spec §6.1) — deep-page beyond this should use
 *  filter/cursor, not jump. Server returns HTTP 400 if exceeded. */
export const MAX_OFFSET = 100_000;
/** Mobile default (viewport < Tailwind `sm` = 640px). 10 fits on phone
 *  without overscroll noise. */
export const DEFAULT_PAGE_SIZE_MOBILE = 10;
/** Desktop default (viewport >= 640px). 25 fills typical 13" laptop
 *  view without forcing operators to flip pages constantly. */
export const DEFAULT_PAGE_SIZE_DESKTOP = 25;
/** Legacy export kept for callers that explicitly want the old uniform
 *  50; new code should rely on the viewport-aware default. */
export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_DESKTOP;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** Pick the viewport-aware default page size. SSR-safe (falls back to
 *  desktop default when `window` isn't defined). Detected once at
 *  hook init; subsequent resize doesn't re-evaluate — operator can
 *  manually change page size via the select anytime. */
function defaultPageSizeForViewport(): number {
  if (typeof window === "undefined") return DEFAULT_PAGE_SIZE_DESKTOP;
  return window.innerWidth < 640 ? DEFAULT_PAGE_SIZE_MOBILE : DEFAULT_PAGE_SIZE_DESKTOP;
}

export interface UsePaginationOptions {
  /** Default page size when no URL param is present. Defaults to 50. */
  defaultLimit?: number;
  /** When true, page + size sync to URL search params via react-router.
   *  When false the hook keeps state in a closure (back-button-unaware).
   *  Default: true. */
  syncUrl?: boolean;
  /** Optional URL param prefix for multi-table pages (rare). Resolves to
   *  `${prefix}page` and `${prefix}size`. Default: empty (params are
   *  `page` + `size`). */
  urlPrefix?: string;
}

export interface UsePaginationResult {
  /** Page number, 1-indexed. */
  page: number;
  /** Server `limit` param. */
  limit: number;
  /** Server `offset` param. Computed as (page - 1) * limit. */
  offset: number;
  /** Set the page. Clamps to ≥ 1; clamps to MAX_OFFSET / limit. */
  setPage: (page: number) => void;
  /** Set the per-page size. Clamps to MAX_PAGE_SIZE. Auto-resets page
   *  to 1 so the user doesn't end up beyond the new total. */
  setLimit: (limit: number) => void;
  /** Wraps a state-setter so calling the result also resets page to 1.
   *  Use for filter / search setters: `const setQ = withFilter(setQRaw)`. */
  withFilter: <Args extends unknown[]>(fn: (...args: Args) => void) => (...args: Args) => void;
}

export function usePagination(opts: UsePaginationOptions = {}): UsePaginationResult {
  const {
    // Viewport-aware default when caller doesn't pin one. Computed
    // once at mount via window.innerWidth — Tailwind `sm` (640px)
    // boundary. Operator can still override via the page-size select.
    defaultLimit = defaultPageSizeForViewport(),
    syncUrl = true,
    urlPrefix = "",
  } = opts;
  const pageKey = `${urlPrefix}page`;
  const sizeKey = `${urlPrefix}size`;

  const [params, setParams] = useSearchParams();

  // Read params. When syncUrl=false we still read once on mount so a
  // direct URL hit honours ?page= (cheap correctness; the hook just
  // won't write back).
  const page = useMemo(() => {
    const raw = params.get(pageKey);
    const n = raw ? parseInt(raw, 10) : 1;
    if (!Number.isFinite(n) || n < 1) return 1;
    return n;
  }, [params, pageKey]);

  const limit = useMemo(() => {
    const raw = params.get(sizeKey);
    const n = raw ? parseInt(raw, 10) : defaultLimit;
    if (!Number.isFinite(n) || n < 1) return defaultLimit;
    return Math.min(MAX_PAGE_SIZE, n);
  }, [params, sizeKey, defaultLimit]);

  const offset = Math.min(MAX_OFFSET, (page - 1) * limit);

  const writeParams = useCallback(
    (nextPage: number, nextLimit: number): void => {
      if (!syncUrl) return;
      // Replace in-place — keep URL clean (no `?page=1&size=50` when
      // defaults are in effect; reduces noise on shared links).
      const next = new URLSearchParams(params);
      if (nextPage > 1) next.set(pageKey, String(nextPage));
      else next.delete(pageKey);
      if (nextLimit !== defaultLimit) next.set(sizeKey, String(nextLimit));
      else next.delete(sizeKey);
      setParams(next, { replace: false });
    },
    [params, setParams, syncUrl, pageKey, sizeKey, defaultLimit],
  );

  const setPage = useCallback(
    (p: number): void => {
      const clamped = Math.max(1, p);
      const maxPage = Math.floor(MAX_OFFSET / limit) + 1;
      writeParams(Math.min(maxPage, clamped), limit);
    },
    [limit, writeParams],
  );

  const setLimit = useCallback(
    (l: number): void => {
      const clamped = Math.max(1, Math.min(MAX_PAGE_SIZE, l));
      // Reset to first page so the user isn't beyond the new total.
      writeParams(1, clamped);
    },
    [writeParams],
  );

  const withFilter = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void): ((...args: Args) => void) => {
      return (...args: Args): void => {
        fn(...args);
        // Reset page after the inner setter runs so consumers always
        // land on page 1 of the new filtered result.
        writeParams(1, limit);
      };
    },
    [limit, writeParams],
  );

  return { page, limit, offset, setPage, setLimit, withFilter };
}
